import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { CONTACT_CATEGORIES, SITE } from "@/data/site";
import { submitContact } from "@/lib/api";
import { useSiteSettings } from "@/context/SiteContext";
import { useLang } from "@/context/LanguageContext";
import { T } from "@/components/T";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const emptyForm = { name: "", email: "", phone: "", category: CONTACT_CATEGORIES[0], subject: "", message: "" };

export default function Contact() {
  useDocumentTitle("Contact");
  const settings = useSiteSettings();
  const { t } = useLang();
  const publisherName = settings?.publisher_name || SITE.name;
  const contactEmail = settings?.contact_email || SITE.contactEmail;
  const contactPhone = settings?.contact_phone || "";
  const addressLines = settings?.address_lines?.length ? settings.address_lines : SITE.addressLines;
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      setSubmitted(true);
      toast.success(t("contact.success", "Message received. We will respond shortly."));
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="container-wide pt-16 pb-16">
        <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Correspondence</div>
        <h1 className="mt-3 font-heading text-5xl sm:text-6xl leading-[1.02]" style={{ color: "var(--gi-maroon)" }}>Contact the Publisher</h1>
        <div className="hair-divider my-8 max-w-md" />
        <p className="max-w-3xl text-lg leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>
          Whether you are a reader, researcher, educator, library or member of the media, please choose the most appropriate category so your enquiry reaches the right desk.
        </p>
      </section>

      <section className="container-wide pb-28 grid gap-16 lg:grid-cols-[1fr_1.2fr]">
        {/* Colophon */}
        <aside className="lg:sticky lg:top-24 self-start" data-testid="contact-colophon">
          <div className="border p-10" style={{ borderColor: "var(--gi-border)", background: "var(--gi-ivory-2)" }}>
            <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Colophon</div>
            <div className="mt-4 font-heading text-2xl" style={{ color: "var(--gi-maroon)" }} data-testid="contact-publisher-name">{publisherName}</div>
            <div className="hair-divider my-6" />
            <div className="flex items-start gap-3 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
              <MapPin size={16} style={{ color: "var(--gi-maroon)", marginTop: 2 }} />
              <div data-testid="contact-address">
                {addressLines.map((l, i) => <div key={`addr-${i}-${l}`}>{l}</div>)}
              </div>
            </div>
            {contactEmail && (
              <div className="mt-4 flex items-start gap-3 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
                <Mail size={16} style={{ color: "var(--gi-maroon)", marginTop: 2 }} />
                <a href={`mailto:${contactEmail}`} className="hover:underline" data-testid="contact-email">{contactEmail}</a>
              </div>
            )}
            {contactPhone && (
              <div className="mt-3 flex items-start gap-3 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
                <Phone size={16} style={{ color: "var(--gi-maroon)", marginTop: 2 }} />
                <a href={`tel:${contactPhone.replace(/\s+/g, "")}`} className="hover:underline" data-testid="contact-phone-link">{contactPhone}</a>
              </div>
            )}
            {(!contactEmail && !contactPhone) && (
              <div className="mt-4 flex items-start gap-3 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
                <Mail size={16} style={{ color: "var(--gi-maroon)", marginTop: 2 }} />
                <div>{SITE.contactEmail}</div>
              </div>
            )}
            <div className="hair-divider my-6" />
            <p className="text-xs" style={{ color: "var(--gi-muted)" }}>
              Please write to us using this address or the form on the left.
            </p>
          </div>
        </aside>

        <div>
          {submitted ? (
            <div className="p-10 border" style={{ borderColor: "var(--gi-gold)", background: "#fff" }} data-testid="contact-submitted">
              <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Thank you</div>
              <div className="mt-3 font-heading text-3xl" style={{ color: "var(--gi-maroon)" }}>Message Received</div>
              <p className="mt-4 text-base" style={{ color: "var(--gi-charcoal-2)" }}>
                Your enquiry has been recorded and will be routed to the appropriate desk. We aim to respond within a few working days.
              </p>
              <button onClick={() => { setForm(emptyForm); setSubmitted(false); }} className="btn-outline mt-8" data-testid="contact-send-another">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field labelKey="contact.name" labelFallback="Name" required value={form.name} onChange={v => set("name", v)} testId="contact-name" />
                <Field labelKey="contact.email" labelFallback="Email" type="email" required value={form.email} onChange={v => set("email", v)} testId="contact-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field labelKey="contact.phone" labelFallback="Phone (optional)" value={form.phone} onChange={v => set("phone", v)} testId="contact-phone" />
                <div>
                  <label className="gi-label"><T textKey="contact.category" fallback="Enquiry Category" /></label>
                  <select className="gi-input" value={form.category} onChange={e => set("category", e.target.value)} data-testid="contact-category">
                    {CONTACT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <Field labelKey="contact.subject" labelFallback="Subject" value={form.subject} onChange={v => set("subject", v)} testId="contact-subject" />
              <div>
                <label className="gi-label"><T textKey="contact.message" fallback="Message" /></label>
                <textarea rows={6} className="gi-input" value={form.message} onChange={e => set("message", e.target.value)} required data-testid="contact-message" />
              </div>
              <div>
                <button type="submit" disabled={loading} className="btn-maroon" data-testid="contact-submit-btn">
                  {loading ? "Sending…" : <T textKey="contact.submit" fallback="Send Message" />} <ArrowRight size={14} />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, labelKey, labelFallback, value, onChange, required, type = "text", testId }) => (
  <div>
    <label className="gi-label">
      {labelKey ? <T textKey={labelKey} fallback={labelFallback} /> : label}
      {required && " *"}
    </label>
    <input className="gi-input" type={type} value={value} onChange={e => onChange(e.target.value)} required={required} data-testid={testId} />
  </div>
);
